import React, { useState, useMemo } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import ListColorPicker, { getRandomListColor } from './ListColorPicker';
import './AddToListsModal.css';

interface Group {
  id: number;
  name: string;
  color?: string;
}

interface AddToListsModalProps {
  isOpen: boolean;
  onClose: () => void;
  friendName: string;
  groups: Group[];
  selectedGroupIds: number[];
  onToggleSelection: (id: number) => void;
  onConfirm: () => void;
  onCreateGroup?: (name: string, color: string) => Promise<void>;
  loading?: boolean;
  hasChanges?: boolean;
}

/**
 * Shared modal component for adding a friend to lists/groups
 * Used in Friends.tsx and potentially Map.tsx
 */
const AddToListsModal: React.FC<AddToListsModalProps> = ({
  isOpen,
  onClose,
  friendName,
  groups,
  selectedGroupIds,
  onToggleSelection,
  onConfirm,
  onCreateGroup,
  loading = false,
  hasChanges = true,
}) => {
  const { t } = useLanguage();
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupColor, setNewGroupColor] = useState(getRandomListColor());
  const [creating, setCreating] = useState(false);

  // Filter groups by search query
  const filteredGroups = useMemo(() => {
    if (!searchQuery.trim()) return groups;
    const query = searchQuery.toLowerCase();
    return groups.filter(group =>
      group.name.toLowerCase().includes(query)
    );
  }, [groups, searchQuery]);

  // Reset state when modal closes
  React.useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
      setShowCreateForm(false);
      setNewGroupName('');
      setNewGroupColor(getRandomListColor());
    }
  }, [isOpen]);

  const handleCreateGroup = async () => {
    if (!onCreateGroup || !newGroupName.trim()) return;

    setCreating(true);
    try {
      await onCreateGroup(newGroupName.trim(), newGroupColor);
      setShowCreateForm(false);
      setNewGroupName('');
      setNewGroupColor(getRandomListColor());
    } finally {
      setCreating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="add-to-lists-modal-overlay" onClick={onClose}>
      <div className="add-to-lists-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="add-to-lists-modal-header">
          <h2>{t('friends.groups.addToGroupsModal', { name: friendName })}</h2>
          <button className="add-to-lists-modal-close" onClick={onClose}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Search Input */}
        <div className="add-to-lists-search">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="search-icon">
            <circle cx="10" cy="10" r="7"/>
            <line x1="15" y1="15" x2="22" y2="22"/>
          </svg>
          <input
            type="text"
            placeholder={t('friends.groups.searchListsPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            autoFocus
          />
          {searchQuery && (
            <button className="search-clear" onClick={() => setSearchQuery('')}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          )}
        </div>

        {/* Groups List */}
        <div className="add-to-lists-list">
          {filteredGroups.length === 0 ? (
            <p className="add-to-lists-empty">
              {groups.length === 0
                ? t('friends.groups.empty')
                : t('map.search.noResults')
              }
            </p>
          ) : (
            filteredGroups.map((group) => {
              const isSelected = selectedGroupIds.includes(group.id);

              return (
                <div
                  key={group.id}
                  className={`add-to-lists-item ${isSelected ? 'selected' : ''}`}
                  onClick={() => onToggleSelection(group.id)}
                >
                  <div className="add-to-lists-checkbox">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => onToggleSelection(group.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  <span
                    className="add-to-lists-color"
                    style={{ backgroundColor: group.color || 'var(--accent-primary)' }}
                  />
                  <span className="add-to-lists-name">{group.name}</span>
                </div>
              );
            })
          )}
        </div>

        {/* Inline Create Group Form */}
        {showCreateForm && onCreateGroup && (
          <div className="add-to-lists-create-form">
            <input
              type="text"
              placeholder={t('friends.groups.namePlaceholder')}
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              className="create-group-input"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newGroupName.trim()) {
                  handleCreateGroup();
                }
              }}
            />
            <ListColorPicker
              color={newGroupColor}
              onChange={setNewGroupColor}
              size="small"
            />
            <button
              className="btn-create-confirm"
              onClick={handleCreateGroup}
              disabled={creating || !newGroupName.trim()}
              title={t('friends.groups.create')}
            >
              {creating ? '...' : (
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              )}
            </button>
            <button
              className="btn-create-cancel"
              onClick={() => {
                setShowCreateForm(false);
                setNewGroupName('');
                setNewGroupColor(getRandomListColor());
              }}
              disabled={creating}
              title={t('common.cancel')}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
        )}

        {/* Footer */}
        <div className="add-to-lists-modal-footer">
          {!showCreateForm && onCreateGroup && (
            <button
              className="btn-secondary"
              onClick={() => setShowCreateForm(true)}
            >
              {t('friends.groups.createNew')}
            </button>
          )}
          <button
            className="btn-primary"
            onClick={onConfirm}
            disabled={loading || !hasChanges}
          >
            {loading ? t('friends.groups.saving') : t('friends.groups.save')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddToListsModal;
